// CareTeamEditor — a client's physicians, any number of them, each with a
// role. Replaces the two fixed free-text blocks (PCP, ordering/specialist)
// on the client form.
//
//   <CareTeamEditor ref={ref} clientId={id | null} onChange={team => ...} />
//
// For an existing client every change saves straight away (links are their
// own rows). For a client being created there is no id yet, so the list is
// held locally and the form calls ref.current.commit(newClientId) after the
// insert. onChange fires with the current team either way, so the form can
// mirror the PCP and ordering physician into the legacy text columns that
// packets, CMNs and reports still read.
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  getCareTeamDetailed, addCareTeamMember, setCareTeamRole, removeCareTeamMember, listPhysicians, listFacilities, getPhysician, markVerified,
  fmtPhone, CARE_ROLES, CARE_ROLE_LABEL,
} from '../../lib/directory';
import { RefCode, Pill, Empty, VerifiedPill } from './ui';
import PhysicianPicker from './PhysicianPicker';
import PhysicianForm from './PhysicianForm';

const ROLE_TONE = { pcp: 'info', ordering: 'copper', specialist: 'neutral', referring: 'ok', other: 'neutral' };

const CareTeamEditor = forwardRef(function CareTeamEditor({ clientId, onChange, compact = false, showDetails = false, canEdit = true }, ref) {
  const { profile } = useAuth();
  const actor = profile?.full_name || 'staff';
  const [team, setTeam] = useState([]);           // [{ id?, physician, role }]
  const [physicians, setPhysicians] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [adding, setAdding] = useState(null);     // role being added
  const [creating, setCreating] = useState(null); // query for a new physician
  const [editingDoc, setEditingDoc] = useState(null); // physician whose directory record is being edited
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(!!clientId);

  useEffect(() => {
    listPhysicians().then(({ data }) => setPhysicians((data || []).filter(p => p.status === 'active')));
    listFacilities().then(({ data }) => setFacilities(data || []));
  }, []);
  useEffect(() => {
    if (!clientId) return;
    getCareTeamDetailed(clientId).then(({ data }) => { const t = (data || []).filter(l => l.physician); setTeam(t); setLoading(false); onChange?.(t); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const publish = (t) => { setTeam(t); onChange?.(t); };

  useImperativeHandle(ref, () => ({
    team,
    // create links for a client that has just been inserted
    commit: async (newClientId) => {
      for (const m of team) if (!m.id) await addCareTeamMember(newClientId, m.physician.id, m.role, actor);
    },
  }), [team, actor]);

  const roleTaken = (role) => ['pcp', 'ordering'].includes(role) && team.some(m => m.role === role);
  const nextRole = () => (!roleTaken('pcp') ? 'pcp' : !roleTaken('ordering') ? 'ordering' : 'specialist');

  const add = async (physician, role) => {
    setErr('');
    if (team.some(m => m.physician.id === physician.id && m.role === role)) { setErr('That physician is already on the care team with this role.'); return; }
    if (roleTaken(role)) { setErr(role === 'pcp' ? 'There is already a PCP — change that row\'s role first.' : 'There is already an ordering physician — change that row\'s role first.'); return; }
    if (clientId) {
      const { data, error } = await addCareTeamMember(clientId, physician.id, role, actor);
      if (error) { setErr(error.message); return; }
      publish([...team, { id: data.id, physician, role }]);
    } else publish([...team, { physician, role }]);
    setAdding(null);
  };
  const changeRole = async (m, role) => {
    setErr('');
    if (role !== m.role && roleTaken(role)) { setErr(role === 'pcp' ? 'There is already a PCP.' : 'There is already an ordering physician.'); return; }
    if (m.id) { const { error } = await setCareTeamRole(m.id, role); if (error) { setErr(error.message); return; } }
    publish(team.map(x => x === m ? { ...x, role } : x));
  };
  const remove = async (m) => {
    setErr('');
    if (m.id) { const { error } = await removeCareTeamMember(m.id); if (error) { setErr(error.message); return; } }
    publish(team.filter(x => x !== m));
  };

  const line = (p) => [p.specialty, p.facility_name, p.npi ? `NPI ${p.npi}` : 'no NPI'].filter(Boolean).join(' · ');

  return (
    <div>
      {err && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--danger)' }}>{err}</div>}
      {loading ? <Empty><div className="loading-spinner" style={{ margin: '0 auto' }} /></Empty>
        : team.length === 0 && !adding ? <Empty>No physicians linked yet. Add the PCP first — the form fills itself from the directory.</Empty>
        : team.map((m, i) => {
          const p = m.physician;
          return (
            <div key={m.id || `${p.id}-${m.role}-${i}`} style={{ display: 'grid', gridTemplateColumns: compact ? '1fr auto' : 'auto minmax(0,1fr) auto auto', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px dashed var(--border)' }}>
              {!compact && <RefCode code={p.ref_code} />}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.display_name}{p.credentials ? <span style={{ fontWeight: 400, color: 'var(--text3)' }}> {p.credentials}</span> : null}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line(p)}</div>
                {!compact && <div style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>{p.phone ? `tel ${fmtPhone(p.phone)}` : ''}{p.phone && p.fax ? '  ·  ' : ''}{p.fax ? `fax ${fmtPhone(p.fax)}` : ''}{!p.phone && !p.fax ? <span style={{ fontFamily: 'inherit', color: 'var(--warn)' }}>no phone or fax on record</span> : null}</div>}
                {showDetails && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{[p.address_line1, p.address_line2, [p.city, p.state].filter(Boolean).join(', '), p.zip].filter(Boolean).join(', ') || 'no address on record'}</span>
                    <VerifiedPill verified_at={p.verified_at} />
                    {canEdit && <button type="button" onClick={() => setEditingDoc(p)} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', padding: 0 }}>✎ Edit details</button>}
                  </div>
                )}
              </div>
              {canEdit ? (
                <select value={m.role} onChange={e => changeRole(m, e.target.value)} className="form-input" style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                  {CARE_ROLES.map(r => <option key={r.value} value={r.value} disabled={r.value !== m.role && roleTaken(r.value)}>{r.label}</option>)}
                </select>
              ) : <Pill tone={ROLE_TONE[m.role] || 'neutral'}>{CARE_ROLE_LABEL[m.role] || m.role}</Pill>}
              {!compact && canEdit ? (
                <button type="button" onClick={() => remove(m)} title="Remove from care team" style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 18, cursor: 'pointer', opacity: 0.6, lineHeight: 1 }}>×</button>
              ) : <span />}
            </div>
          );
        })}

      {adding ? (
        <div style={{ marginTop: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text2)' }}>Add as</span>
            {CARE_ROLES.map(r => (
              <button key={r.value} type="button" disabled={roleTaken(r.value)} onClick={() => setAdding(r.value)}
                style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, border: '1px solid', cursor: roleTaken(r.value) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: roleTaken(r.value) ? 0.4 : 1,
                  background: adding === r.value ? 'var(--navy)' : 'transparent', color: adding === r.value ? '#fff' : 'var(--text2)', borderColor: adding === r.value ? 'var(--text)' : 'var(--border2)' }}>{r.label}</button>
            ))}
            <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{CARE_ROLES.find(r => r.value === adding)?.hint}</span>
            <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setAdding(null)}>Cancel</button>
          </div>
          <PhysicianPicker physicians={physicians || []} value={null} autoFocus exclude={team.filter(m => m.role === adding).map(m => m.physician.id)}
            onChange={(p) => p && add(p, adding)} onAddNew={(q) => setCreating(q || ' ')} />
        </div>
      ) : (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {canEdit && <button type="button" className="btn btn-outline btn-sm" onClick={() => setAdding(nextRole())}>+ Add physician</button>}
          {team.length > 0 && <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>
            {['pcp', 'ordering'].map(r => <Pill key={r} tone={team.some(m => m.role === r) ? ROLE_TONE[r] : 'warn'} style={{ marginRight: 4 }}>{CARE_ROLE_LABEL[r]}: {team.some(m => m.role === r) ? 'set' : 'none'}</Pill>)}
          </span>}
        </div>
      )}

      {editingDoc && (
        <PhysicianForm physician={editingDoc} facilities={facilities}
          onSaved={async (doc) => {
            setEditingDoc(null);
            const { data: fresh } = await getPhysician(doc.id);
            if (fresh) publish(team.map(m => m.physician.id === fresh.id ? { ...m, physician: fresh } : m));
          }}
          onClose={() => setEditingDoc(null)}
          onVerify={async (doc) => {
            await markVerified('physicians', doc.id, actor);
            setEditingDoc(null);
            const { data: fresh } = await getPhysician(doc.id);
            if (fresh) publish(team.map(m => m.physician.id === fresh.id ? { ...m, physician: fresh } : m));
          }} />
      )}
      {creating !== null && (
        <PhysicianForm initialQuery={creating.trim()} facilities={facilities}
          onSaved={async (doc) => {
            setCreating(null);
            // re-read through the view so facility address/phone/fax fallbacks are present
            const { data: p } = await getPhysician(doc.id);
            const row = p || { ...doc, display_name: `${doc.last_name}, ${doc.first_name}`, client_count: 0 };
            setPhysicians(ps => [...(ps || []), row]);
            add(row, adding || nextRole());
          }}
          onClose={() => setCreating(null)} />
      )}
    </div>
  );
});

export default CareTeamEditor;
